import { useTranslation, Trans } from 'react-i18next'
import { Section } from '../ui/Section'
import { Container } from '../ui/Container'
import { SectionTitle } from '../ui/SectionTitle'
import { motion } from 'framer-motion'
import { Target, Shield, Handshake, Lightbulb, Clock, MapPin, TrendingUp, Building2, ChevronLeft, ChevronRight } from 'lucide-react'
import { useRef } from 'react'

const milestones = [
  { year: '2004', icon: Building2 },
  { year: '2013', icon: MapPin },
  { year: '2014', icon: TrendingUp },
  { year: '2019', icon: MapPin },
  { year: '2020', icon: Building2 },
  { year: '2021', icon: TrendingUp },
  { year: '2022', icon: MapPin },
  { year: '2024', icon: Building2 },
]

const teamMembers = [
  { name: 'Boris Malaguti', roleKey: 'ceo', image: '/images/team/team_boris_malaguti.jpg' },
  { name: 'Giorgia Aragona', roleKey: 'generalManager', image: '/images/team/team_giorgia_aragona.jpg' },
  { name: 'Giacomo Capasso', roleKey: 'salesPurchaseManager', image: '/images/team/team_giacomo_capasso.jpg' },
  { name: 'Virginia Franzese', roleKey: 'salesPurchaseManager', image: '/images/team/team_virginia_franzese.jpg' },
  { name: 'Eugenia Privalova', roleKey: 'salesPurchaseManager', image: '/images/team/team_eugenia_privalova.jpg' },
  { name: 'Elisa Macchioni', roleKey: 'marketingManager', image: '/images/team/team_elisa_macchioni.jpg' },
  { name: 'Fiammetta Lamberti', roleKey: 'cfo', image: '/images/team/team_fiammetta_lamberti.jpg' },
  { name: 'Elisa Malagoli', roleKey: 'accountingManager', image: '/images/team/team_elisa_malagoli.jpg' },
  { name: 'Ivan Malaguti', roleKey: 'salesManager', image: '/images/team/team_ivan_malaguti.jpg' },
  { name: 'Alessio Monteleoni', roleKey: 'salesManager', image: '/images/team/team_alessio_monteleoni.jpg' },
  { name: 'Andrea Paroli', roleKey: 'salesManager', image: '/images/team/team_andrea_paroli.jpg' },
  { name: 'Emanuele Gallone', roleKey: 'salesManager', image: '/images/team/team_emanuele_gallone.jpg' },
  { name: 'Raffaele Piccolo', roleKey: 'mechanicalEngineer', image: '/images/team/team_raffaele_piccolo.jpg' },
  { name: 'Matteo Rivi', roleKey: 'logisticManager', image: '/images/team/team_matteo_rivi.jpg' },
]

export function CompanyHistory() {
  const { t } = useTranslation()
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 320
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      })
    }
  }

  const values = [
    { key: 'innovation', icon: Lightbulb },
    { key: 'quality', icon: Shield },
    { key: 'reliability', icon: Target },
    { key: 'partnership', icon: Handshake },
  ]

  return (
    <>
      {/* Company History Section */}
      <Section>
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <SectionTitle title={t('about.history.title')} />
              <div className="space-y-4 text-gray-600">
                <p>{t('about.history.p1')}</p>
                <p>{t('about.history.p2')}</p>
                <p>{t('about.history.p3')}</p>
              </div>
            </div>

            <div className="relative">
              <img
                src="/images/general/hero_azienda.jpg"
                alt="Simertec Company"
                className="rounded-lg shadow-lg w-full"
              />
            </div>
          </div>

          {/* Values */}
          <div className="mt-20">
            <h3 className="text-2xl font-bold text-dark text-center mb-12">
              {t('about.values.title')}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <motion.div
                  key={value.key}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-20 h-20 mx-auto mb-4 bg-primary rounded-full flex items-center justify-center">
                    <value.icon className="w-10 h-10 text-dark" />
                  </div>
                  <h4 className="font-semibold text-dark">
                    {t(`about.values.${value.key}`)}
                  </h4>
                </motion.div>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* Timeline Section */}
      <Section className="bg-gray-50">
        <Container>
          <SectionTitle
            title={t('about.milestones.title')}
            subtitle={t('about.milestones.subtitle')}
            centered
          />

          <div className="relative mt-16">
            {/* Timeline line - desktop */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-primary/30" />

            {/* Timeline items */}
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={milestone.year}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: '-100px' }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`relative flex flex-col md:flex-row items-center gap-8 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  {/* Content */}
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div
                      className={`bg-white rounded-xl shadow-md p-6 inline-block max-w-md ${
                        index % 2 === 0 ? 'md:ml-auto' : 'md:mr-auto'
                      }`}
                    >
                      <span className="inline-flex items-center gap-2 text-primary font-bold text-xl mb-2">
                        <Clock className="w-5 h-5" />
                        {milestone.year}
                      </span>
                      <h3 className="text-lg font-semibold text-dark mb-2">
                        {t(`about.milestones.${milestone.year}.title`)}
                      </h3>
                      <p className="text-gray-600 text-sm">
                        {t(`about.milestones.${milestone.year}.description`)}
                      </p>
                    </div>
                  </div>

                  {/* Center dot */}
                  <div className="hidden md:flex w-12 h-12 bg-primary rounded-full items-center justify-center z-10 shadow-lg shrink-0">
                    <milestone.icon className="w-6 h-6 text-dark" />
                  </div>

                  {/* Empty space for alignment */}
                  <div className="flex-1 hidden md:block" />
                </motion.div>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* Team Section */}
      <Section>
        <Container>
          <div className="text-center mb-12">
            <SectionTitle
              title={t('about.team.title')}
              subtitle={t('about.team.subtitle')}
              centered
            />
            <p className="text-gray-600 max-w-3xl mx-auto mt-6">
              <Trans i18nKey="about.team.description" components={{ strong: <strong /> }} />
            </p>
          </div>

          {/* Team Carousel */}
          <div className="relative">
            {/* Navigation Buttons */}
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-primary hover:text-dark transition-colors"
              aria-label={t('aria.scrollLeft')}
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-primary hover:text-dark transition-colors"
              aria-label={t('aria.scrollRight')}
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            {/* Scrollable Container */}
            <div
              ref={scrollContainerRef}
              className="flex gap-6 overflow-x-auto pb-4 px-4 snap-x snap-mandatory scrollbar-hide"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {teamMembers.map((member) => (
                <div
                  key={member.name}
                  className="flex-shrink-0 w-[280px] snap-start"
                >
                  <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300 h-full">
                    {/* Image Container - aspect ratio verticale compatto per ritratti */}
                    <div className="relative aspect-[4/5] overflow-hidden bg-gray-100">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.style.display = 'none'
                          const parent = target.parentElement
                          if (parent) {
                            parent.classList.add('flex', 'items-center', 'justify-center')
                            const fallback = document.createElement('div')
                            fallback.className = 'flex flex-col items-center justify-center text-dark/40'
                            fallback.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>`
                            parent.appendChild(fallback)
                          }
                        }}
                      />
                    </div>

                    {/* Info */}
                    <div className="p-5 text-center">
                      <h3 className="text-lg font-bold text-dark mb-1">{member.name}</h3>
                      <p className="text-primary font-medium text-sm">
                        {t(`about.team.roles.${member.roleKey}`)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Scroll Indicator */}
            <div className="flex justify-center gap-2 mt-4">
              {teamMembers.map((_, index) => (
                <div
                  key={index}
                  className="w-2 h-2 rounded-full bg-primary/30"
                />
              ))}
            </div>
          </div>
        </Container>
      </Section>
    </>
  )
}
